/**
 * SpectrumReveal (mobile RN) — the Monday moment. Unseen weekly reveals
 * animate old→new with a one-sentence report; fires once. Mirrors the web
 * overlay.
 */
import React, { useEffect, useState } from 'react';
import { ScrollView, StyleSheet, Text, View } from 'react-native';
import { supabase } from '../lib/supabase';
import { makeThemedStyles } from '../v10/theme';
import { Atmos, GoldButton, Kicker, SectionTitle } from '../v10/ui';
import { loadPendingReveals, markRevealsSeen, PendingReveal } from './spectrum-data';
import { getTool } from './spectrum-tools';
import { SpectrumLine } from './SpectrumLine';

export function SpectrumReveal({ userId, onClose }: { userId: string | undefined; onClose: () => void }) {
  const styles = useStyles();
  const [reveals, setReveals] = useState<PendingReveal[] | null>(null);

  useEffect(() => {
    if (!userId) return;
    let alive = true;
    loadPendingReveals(supabase, userId).then((r) => {
      if (alive) setReveals(r);
    });
    return () => {
      alive = false;
    };
  }, [userId]);

  const dismiss = async () => {
    if (userId && reveals && reveals.length) {
      await markRevealsSeen(supabase, userId, reveals.map((r) => r.id));
    }
    onClose();
  };

  if (reveals === null || reveals.length === 0) return null;

  return (
    <Atmos style={styles.overlay}>
      <ScrollView contentContainerStyle={styles.body}>
        <Kicker style={styles.kicker}>MONDAY REVEAL</Kicker>
        <SectionTitle style={styles.title}>The week moved.</SectionTitle>

        {reveals.map((r) => {
          const tool = getTool(r.toolId);
          return (
            <View key={r.id} style={styles.item}>
              <SpectrumLine
                toolName={tool.name}
                maladyName={tool.maladies[0]?.name ?? ''}
                displayed={r.newPosition}
                animateFrom={r.oldPosition}
                repCount={10}
                acquisition={r.event === 'secured' ? 'secured' : 'stable'}
                secured={r.event === 'secured'}
              />
              <Text style={styles.report}>{r.report}</Text>
            </View>
          );
        })}

        <GoldButton label="Set the week ahead" onPress={dismiss} style={styles.button} />
      </ScrollView>
    </Atmos>
  );
}

const useStyles = makeThemedStyles(({ t }) =>
  StyleSheet.create({
    overlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 60,
    },
    body: { padding: 20, gap: 12 },
    kicker: { textAlign: 'center', letterSpacing: 3 },
    title: { marginBottom: 8, justifyContent: 'center' },
    item: { gap: 6 },
    report: { paddingHorizontal: 4, fontSize: 12, color: t.ivorySoft },
    button: { alignSelf: 'center', marginTop: 20 },
  }),
);
